import { Divider, Tab, TabGroup, TabList, TextInput } from '@tremor/react';
import React, { useContext, useState, useEffect } from "react";
import { ShopContext } from "../context/ShopContext";
import { Button } from "@/components/ui/button";
import { toast } from 'react-toastify';
import { assets } from '../assets/assets';
import AddressBook from './AddressBook';
import { FiEdit, FiMail, FiPhone, FiUser, FiLock, FiMapPin, FiLogOut } from 'react-icons/fi';

const avatars = Array.from({ length: 25 }, (_, i) => `avatar${i+1}`);

export default function UserSettings() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [avatar, setAvatar] = useState('avatar1');
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);

  const {
    navigate,
    setToken,
    setCartItems,
    userInfo,
    token
  } = useContext(ShopContext);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    if (userInfo) {
      setForm(f => ({
        ...f,
        name: userInfo.name || '',
        email: userInfo.email || '',
        phone: userInfo.phone || ''
      }));
      setAvatar(userInfo.avatar || 'avatar1');
    }
  }, [userInfo, token, navigate]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleUpdate = (field) => {
    if (!form[field]) {
      toast.error(`Please enter ${field}`);
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully`);
      setIsLoading(false);
    }, 1000);
  };

  const handlePasswordUpdate = () => {
    if (form.newPassword !== form.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (form.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      toast.success("Password updated successfully");
      setForm(f => ({ ...f, currentPassword: '', newPassword: '', confirmPassword: '' }));
      setIsLoading(false);
    }, 1000);
  };

  const handleAvatarChange = (newAvatar) => {
    setAvatar(newAvatar);
    setShowAvatarSelector(false);
    toast.success("Avatar updated successfully");
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken("");
    setCartItems({});
    navigate("/login");
    toast.success("Logged out successfully");
  };

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white rounded-lg shadow">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-500">Manage your account information and preferences</p>
        </div>
        <div className="flex items-center gap-4 bg-gray-700 p-2 rounded-lg">
          <p className="text-gray-100">Online</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-8 mb-8">
        <div className="flex flex-col items-center sm:items-start">
          <div className="relative group mb-4">
            <img 
              src={avatar || assets.profile_icon} 
              alt="Profile" 
              className="w-24 h-24 rounded-full border-2 border-gray-200 object-cover"
            />
            <button 
              onClick={() => setShowAvatarSelector(!showAvatarSelector)}
              className="absolute bottom-0 right-0 bg-gray-800 text-white p-2 rounded-full hover:bg-gray-700 transition-all"
            >
              <FiEdit size={14} />
            </button>
          </div>
          
          {showAvatarSelector && (
            <div className="grid grid-cols-5 gap-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
              {avatars.map(a => (
                <img
                  key={a}
                  src={a}
                  alt="Avatar"
                  className={`w-10 h-10 rounded-full cursor-pointer ${avatar === a ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => handleAvatarChange(a)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{userInfo?.name}</h2>
            <p className="text-gray-600">{userInfo?.email}</p>
          </div>
          
          <TabGroup index={tab} onIndexChange={setTab}>
            <TabList className="border-b border-gray-200">
              <Tab className="flex items-center gap-2">
                <FiUser size={16} /> Profile
              </Tab>
              <Tab className="flex items-center gap-2">
                <FiMapPin size={16} /> Addresses
              </Tab>
            </TabList>
            
            <div className="mt-6">
              {tab === 0 && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiUser className="text-gray-500" />
                      <h3 className="font-medium text-gray-900">Name</h3>
                    </div>
                    <div className="flex gap-2">
                      <TextInput 
                        name="name" 
                        value={form.name} 
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="flex-1"
                      />
                      <Button 
                        onClick={() => handleUpdate('name')}
                        disabled={isLoading || form.name === userInfo?.name}
                        className="min-w-[120px]"
                      >
                        {isLoading ? 'Saving...' : 'Update'}
                      </Button>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiMail className="text-gray-500" />
                      <h3 className="font-medium text-gray-900">Email</h3>
                    </div>
                    <div className="flex gap-2">
                      <TextInput 
                        name="email" 
                        value={form.email} 
                        onChange={handleChange}
                        placeholder="Your email"
                        className="flex-1"
                      />
                      <Button 
                        onClick={() => handleUpdate('email')}
                        disabled={isLoading || form.email === userInfo?.email}
                        className="min-w-[120px]"
                      >
                        {isLoading ? 'Saving...' : 'Update'}
                      </Button>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiPhone className="text-gray-500" />
                      <h3 className="font-medium text-gray-900">Phone</h3>
                    </div>
                    <div className="flex gap-2">
                      <TextInput 
                        name="phone" 
                        value={form.phone} 
                        onChange={handleChange}
                        placeholder="Your phone number"
                        className="flex-1"
                      />
                      <Button 
                        onClick={() => handleUpdate('phone')}
                        disabled={isLoading || form.phone === userInfo?.phone}
                        className="min-w-[120px]"
                      >
                        {isLoading ? 'Saving...' : 'Update'}
                      </Button>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiLock className="text-gray-500" />
                      <h3 className="font-medium text-gray-900">Password</h3>
                    </div>
                    <div className="space-y-3">
                      <TextInput 
                        type="password"
                        name="currentPassword" 
                        value={form.currentPassword} 
                        onChange={handleChange}
                        placeholder="Current password"
                      />
                      <TextInput 
                        type="password"
                        name="newPassword" 
                        value={form.newPassword} 
                        onChange={handleChange}
                        placeholder="New password (min 8 chars)"
                      />
                      <TextInput 
                        type="password"
                        name="confirmPassword" 
                        value={form.confirmPassword} 
                        onChange={handleChange}
                        placeholder="Confirm new password"
                      />
                      <Button 
                        onClick={handlePasswordUpdate}
                        disabled={isLoading}
                        className="w-full sm:w-auto"
                      >
                        {isLoading ? 'Updating...' : 'Change Password'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {tab === 1 && (
                <div>
                  <AddressBook />
                  <div className="flex justify-center mt-6">
                    <Button 
                      onClick={() => setTab(0)}
                      variant="outline"
                      className="border-gray-300"
                    >
                      Back to Profile
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabGroup>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <Button 
          onClick={logout}
          variant="destructive"
          className="flex items-center gap-2 px-6"
        >
          <FiLogOut size={16} /> Logout
        </Button>
      </div>
    </div>
  );
}